import { useState } from 'react';
import { App, Button, Checkbox, DatePicker, Flex, Input, Segmented, Select, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { HotkeyButton, HotkeyTip } from '@/shared/ui/hotkey';
import { useUnsavedClose } from '@/shared/ui';
import { INVALID_INPUTS_HINT, INVALID_INPUTS_REASON, InvalidInputsContext, MoneyInput, commitActiveField, useInvalidInputs } from '@/shared/ui/inputs';
import { useHotkeyActions } from '@/shared/lib/hotkeys';
import { useLatest } from '@/shared/lib/hooks';
import { CounterpartySelect } from '@/entities/counterparty';
import {
  useCancelDocumentMutation,
  useCashOrderTemplateQuery,
  useCreateDocumentMutation,
  useDeleteDocumentMutation,
  useGetDocumentQuery,
  useListDocumentsQuery,
  usePostDocumentMutation,
  usePreviewDocumentMutation,
  useRestoreDocumentMutation,
  useReturnDocumentToDraftMutation,
  useUnpostDocumentMutation,
  useUpdateDocumentMutation,
} from '@/entities/document';
import type { DocumentCounterpartyRef, DocumentDto, LineWarningEntry, SaveDocumentPayload } from '@/entities/document';
import type { PaymentMethod } from '@/entities/payment';
import { useCashOrderPaymentQuery } from '@/entities/payment';
import { AllocationGrid, allocationRows, allocationSeedKey, allocationTarget, toManualAllocations } from '@/features/payment/allocation-grid';
import type { AllocationRow } from '@/features/payment/allocation-grid';
import { PreviewConsequencesDialog, useSaveWithPreview } from '@/features/document/preview-consequences';
import { isVersionConflict } from '@/features/document/concurrent-edit';
import { AttachmentsBlock } from '@/features/attachments';
import { useStoreContext } from '@/shared/lib/store-context';
import { todayInTz } from '@/shared/lib/date';
import { describeWarnings } from '@/shared/lib/warnings';
import { translateApiError } from '@/shared/api/errorToMessage';
import type { CashOrderHeader, CashOrderType } from './model';
import { directionOf, getCashOrderBlockReason, windowTitle } from './model';

const METHOD_OPTIONS: { value: PaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Наличные' },
  { value: 'CASHLESS', label: 'Безналичные' },
  { value: 'TRANSFER', label: 'Перевод' },
];

export interface CashOrderWindowProps {
  type: CashOrderType;
  id?: string;
  /** §7 «Создать приходный/расходный ордер»: id продажи/прихода/возврата-основания. */
  baseDocumentId?: string;
  onClose: () => void;
}

/** §7: приходный/расходный кассовый ордер — полноценный документ (черновик → проведён →
 * отменён/удалён, как остальные), но без строк: сумма/способ/операция вводятся в шапке, а
 * разнесение по документам-должникам — той же сеткой, что и в `PaymentWindow`. */
export function CashOrderWindow({ type, id, baseDocumentId, onClose }: CashOrderWindowProps) {
  const { message, modal } = App.useApp();
  const ctx = useStoreContext();
  const [documentId, setDocumentId] = useState(id);
  const direction = directionOf(type);

  const { data: doc, isFetching, refetch: refetchDoc } = useGetDocumentQuery(documentId ?? '', { skip: !documentId });
  const [createDocument, { isLoading: isCreating }] = useCreateDocumentMutation();
  const [updateDocument, { isLoading: isUpdating }] = useUpdateDocumentMutation();
  const [postDocument, { isLoading: isPosting }] = usePostDocumentMutation();
  const [unpostDocument, { isLoading: isUnposting }] = useUnpostDocumentMutation();
  const [cancelDocument, { isLoading: isCancelling }] = useCancelDocumentMutation();
  const [deleteDocument, { isLoading: isDeleting }] = useDeleteDocumentMutation();
  const [returnToDraft, { isLoading: isReturningToDraft }] = useReturnDocumentToDraftMutation();
  const [restoreDocument, { isLoading: isRestoring }] = useRestoreDocumentMutation();
  const [previewDocument] = usePreviewDocumentMutation();
  const [isPosting_, setIsPosting_] = useState(false);
  const { runWithPreview, pendingDiff, resolvePending, isRunningPreview } = useSaveWithPreview();

  const [header, setHeader] = useState<CashOrderHeader>({
    docDate: todayInTz(ctx.timezone),
    amount: null,
    method: 'CASH',
    cashOperationType: 'PAYMENT',
    comment: '',
  });
  const [counterpartyRef, setCounterpartyRef] = useState<DocumentCounterpartyRef | undefined>(undefined);
  const [noAutoAllocate, setNoAutoAllocate] = useState(false);
  const [rows, setRows] = useState<AllocationRow[]>([]);
  const [rowsDirty, setRowsDirty] = useState(false);
  // Несохранённые изменения: без этого `Esc` закрыл бы окно вместе с введённым.
  const [dirty, setDirty] = useState(false);

  /** Правка шапки пользователем (в отличие от заполнения из загруженного документа или шаблона). */
  function editHeader(patch: Partial<CashOrderHeader>) {
    setDirty(true);
    setHeader((h) => ({ ...h, ...patch }));
  }

  function seedHeaderFrom(d: DocumentDto) {
    setDirty(false);
    setHeader({
      docDate: d.docDate.slice(0, 10),
      counterpartyId: d.counterpartyId ?? undefined,
      amount: d.totalAmount,
      method: (d.method as PaymentMethod | null) ?? 'CASH',
      cashOperationType: d.cashOperationType ?? 'PAYMENT',
      comment: d.comment ?? '',
    });
    setCounterpartyRef(d.counterparty ?? undefined);
  }

  // Загруженный документ → заполнить шапку один раз (render-phase, не эффект — тот же приём, что и
  // в DocumentWindow/PaymentWindow).
  // Несохранённые правки владельца при обновлении документа с сервера не затираем.
  const [seededDoc, setSeededDoc] = useState<DocumentDto | undefined>(undefined);
  if (doc && doc !== seededDoc && (!dirty || !seededDoc)) {
    setSeededDoc(doc);
    seedHeaderFrom(doc);
  }

  // §7 «Создать приходный/расходный ордер»: новое окно, открытое с основанием, — контрагент, вид
  // операции и сумма остатка долга подтягиваются один раз, тем же паттерном.
  const { data: template } = useCashOrderTemplateQuery(baseDocumentId ?? '', { skip: !baseDocumentId || !!id });
  const [seededTemplateFrom, setSeededTemplateFrom] = useState<string | undefined>(undefined);
  if (template && baseDocumentId && baseDocumentId !== seededTemplateFrom) {
    setSeededTemplateFrom(baseDocumentId);
    setHeader((h) => ({ ...h, counterpartyId: template.counterpartyId ?? undefined, cashOperationType: template.cashOperationType, amount: template.suggestedAmount }));
    setCounterpartyRef(template.counterparty ?? undefined);
  }

  const status = doc?.status ?? 'DRAFT';
  const isNew = !documentId;
  const isDeleted = !!doc?.deletedAt;
  const isReadOnly = status === 'CANCELLED' || isDeleted;
  const busy = isCreating || isUpdating || isPosting || isPosting_ || isUnposting || isCancelling || isDeleting || isReturningToDraft || isRestoring || isRunningPreview;

  // Разнесение и «Не разносить» не хранятся на документе, только на связанном Payment (§7).
  const { data: paymentData } = useCashOrderPaymentQuery(documentId ?? '', { skip: !documentId });
  const existingPayment = paymentData?.payment;
  const target = allocationTarget(header.cashOperationType, direction);
  const { data: sideList } = useListDocumentsQuery(
    { type: target.documentType, counterpartyIds: header.counterpartyId ? [header.counterpartyId] : [], statuses: ['POSTED'], paymentStatuses: ['UNPAID', 'PARTIAL'] },
    { skip: !header.counterpartyId },
  );
  const [seededSideKey, setSeededSideKey] = useState<string | undefined>(undefined);
  const sideKey = sideList
    ? allocationSeedKey(`${header.cashOperationType}:${direction}`, existingPayment?.rowVersion, sideList.items, existingPayment?.allocations ?? [])
    : undefined;
  if (sideKey && sideKey !== seededSideKey && !rowsDirty) {
    setSeededSideKey(sideKey);
    setRows(allocationRows(sideList!.items, existingPayment?.allocations ?? [], target, baseDocumentId, header.amount));
    setNoAutoAllocate(existingPayment?.doNotAllocate ?? false);
  }

  function editRows(next: AllocationRow[]) {
    setRowsDirty(true);
    setDirty(true);
    setRows(next);
  }

  function handleMutationError(e: unknown) {
    if (!isVersionConflict(e)) {
      message.error(translateApiError(e));
      return;
    }
    modal.confirm({
      title: 'Документ изменён в другом окне',
      content: 'Кто-то успел сохранить более новую версию. Открыть свежую версию? Ваши несохранённые изменения будут потеряны.',
      okText: 'Открыть свежую версию',
      cancelText: 'Отмена',
      autoFocusButton: 'cancel',
      onOk: async () => {
        const result = await refetchDoc();
        if (result.data) seedHeaderFrom(result.data);
      },
    });
  }

  const invalidInputs = useInvalidInputs();
  // Причина недоступности («Укажите сумму») нужна и подсказке на отключённой кнопке, и сообщению,
  // если действие вызвали горячей клавишей; `null` — действие доступно.
  const saveBlockReason = invalidInputs.hasInvalid ? INVALID_INPUTS_REASON : null;
  const postBlockReason = saveBlockReason ?? getCashOrderBlockReason(header);
  const canSave = saveBlockReason === null;

  function buildPayload(): SaveDocumentPayload {
    return {
      docDate: header.docDate,
      counterpartyId: header.counterpartyId,
      comment: header.comment || undefined,
      amount: header.amount ?? '0.00',
      method: header.method,
      cashOperationType: header.cashOperationType,
      noAutoAllocate: header.cashOperationType === 'PAYMENT' ? noAutoAllocate : false,
      allocations: toManualAllocations(rows),
    };
  }

  async function ensureCreated(): Promise<string> {
    if (documentId) return documentId;
    const created = await createDocument({ type, baseDocumentId, ...buildPayload() }).unwrap();
    setDocumentId(created.id);
    setDirty(false);
    return created.id;
  }

  /** Кнопки при неверном поле недоступны; сюда приходят и из диалога «Сохранить изменения?», и по
   *  горячей клавише — проверка повторяется здесь, а не только в `disabled` кнопки. */
  function refuseInvalidInput(): boolean {
    if (canSave) return false;
    message.warning(INVALID_INPUTS_HINT);
    return true;
  }

  /** То же для «Провести»/«Сохранить» проведённого: ордер должен быть полон. */
  function refuseIncompleteOrder(): boolean {
    if (refuseInvalidInput()) return true;
    if (postBlockReason === null) return false;
    message.warning(postBlockReason);
    return true;
  }

  async function handleSaveDraft(close: boolean) {
    if (busy || refuseInvalidInput()) return;
    try {
      if (!documentId) {
        await ensureCreated();
      } else {
        await updateDocument({ id: documentId, rowVersion: doc!.rowVersion, body: buildPayload() }).unwrap();
        setRowsDirty(false);
        setDirty(false);
      }
      message.success('Черновик сохранён');
      if (close) onClose();
    } catch (e) {
      handleMutationError(e);
    }
  }

  function warningTexts(warnings: LineWarningEntry[]): string[] {
    return describeWarnings(warnings, () => undefined);
  }

  function confirmWarnings(texts: string[]): Promise<boolean> {
    return new Promise((resolve) => {
      modal.confirm({
        title: 'Провести с предупреждениями?',
        content: (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {texts.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        ),
        okText: 'Провести',
        cancelText: 'Не проводить',
        width: 480,
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  }

  async function handlePost(close: boolean) {
    if (busy || refuseIncompleteOrder()) return;
    setIsPosting_(true);
    try {
      const currentId = await ensureCreated();
      const rowVersion = documentId ? (doc?.rowVersion ?? 1) : 1;
      const payload = documentId ? buildPayload() : undefined;
      const preview = await previewDocument({ id: currentId, body: { action: 'post', rowVersion, document: payload } }).unwrap();
      const texts = warningTexts(preview.warnings);
      if (texts.length > 0 && !(await confirmWarnings(texts))) {
        message.info('Документ не проведён и остаётся черновиком');
        return;
      }
      const result = await postDocument({ id: currentId, body: { rowVersion, document: payload } }).unwrap();
      seedHeaderFrom(result.document);
      setRowsDirty(false);
      message.success(result.warnings.length > 0 ? 'Документ проведён с предупреждениями' : 'Документ проведён');
      if (close) onClose();
    } catch (e) {
      handleMutationError(e);
    } finally {
      setIsPosting_(false);
    }
  }

  async function handleSaveRepost(close: boolean) {
    if (busy || !documentId || !doc || refuseIncompleteOrder()) return;
    const payload = buildPayload();
    try {
      const saved = await runWithPreview(
        documentId,
        'post',
        doc.rowVersion,
        async (stateVersion) => {
          const result = await postDocument({ id: documentId, body: { rowVersion: doc.rowVersion, document: payload, stateVersion } }).unwrap();
          seedHeaderFrom(result.document);
        },
        payload,
      );
      if (saved) {
        setRowsDirty(false);
        message.success('Документ перепроведён');
        if (close) onClose();
      }
    } catch (e) {
      handleMutationError(e);
    }
  }

  /** Деструктивные действия (снять проведение, отменить, удалить) всегда переспрашивают, а фокус
   *  в диалоге стоит на «Отмена»: случайный Enter ничего не разрушает. */
  function handleUnpost() {
    if (busy || !documentId || !doc) return;
    modal.confirm({
      title: 'Снять проведение?',
      content: 'Остатки и расчёты по документу будут пересчитаны, а документ вернётся в черновик.',
      okText: 'Снять проведение',
      cancelText: 'Отмена',
      okButtonProps: { danger: true },
      autoFocusButton: 'cancel',
      onOk: async () => {
        try {
          const saved = await runWithPreview(documentId, 'unpost', doc.rowVersion, (stateVersion) =>
            unpostDocument({ id: documentId, body: { rowVersion: doc.rowVersion, stateVersion } }).unwrap(),
          );
          if (saved) message.success('Проведение снято');
        } catch (e) {
          handleMutationError(e);
        }
      },
    });
  }

  function handleCancel() {
    if (busy || !documentId || !doc) return;
    modal.confirm({
      title: 'Отменить документ?',
      okText: 'Отменить документ',
      cancelText: 'Не отменять',
      okButtonProps: { danger: true },
      autoFocusButton: 'cancel',
      onOk: async () => {
        try {
          const saved = await runWithPreview(documentId, 'cancel', doc.rowVersion, (stateVersion) =>
            cancelDocument({ id: documentId, body: { rowVersion: doc.rowVersion, stateVersion } }).unwrap(),
          );
          if (saved) message.success('Документ отменён');
        } catch (e) {
          handleMutationError(e);
        }
      },
    });
  }

  function handleDelete() {
    if (busy || !documentId || !doc) return;
    modal.confirm({
      title: 'Удалить документ?',
      okText: 'Удалить',
      cancelText: 'Отмена',
      okButtonProps: { danger: true },
      autoFocusButton: 'cancel',
      onOk: async () => {
        try {
          const saved =
            status === 'POSTED'
              ? await runWithPreview(documentId, 'remove', doc.rowVersion, (stateVersion) =>
                  deleteDocument({ id: documentId, body: { rowVersion: doc.rowVersion, stateVersion } }).unwrap(),
                )
              : await deleteDocument({ id: documentId, body: { rowVersion: doc.rowVersion } })
                  .unwrap()
                  .then(() => true);
          if (saved) {
            message.success('Документ удалён');
            onClose();
          }
        } catch (e) {
          handleMutationError(e);
        }
      },
    });
  }

  async function handleReturnToDraft() {
    if (busy || !documentId || !doc) return;
    try {
      const result = await returnToDraft({ id: documentId, rowVersion: doc.rowVersion }).unwrap();
      seedHeaderFrom(result.document);
      message.success('Документ возвращён в черновик');
    } catch (e) {
      handleMutationError(e);
    }
  }

  async function handleRestore() {
    if (busy || !documentId || !doc) return;
    try {
      const result = await restoreDocument({ id: documentId, rowVersion: doc.rowVersion }).unwrap();
      seedHeaderFrom(result.document);
      message.success(result.document.status === 'DRAFT' ? 'Документ восстановлен как черновик — проведите его заново' : 'Документ восстановлен');
    } catch (e) {
      handleMutationError(e);
    }
  }

  // ── Закрытие с вопросом и горячие клавиши ────────────────────────────────────────────────
  const { requestClose, dialog: unsavedDialog } = useUnsavedClose({
    dirty,
    onClose,
    onSave: () => void (status === 'DRAFT' ? handleSaveDraft(true) : handleSaveRepost(true)),
    saveLabel: status === 'DRAFT' ? 'Сохранить черновик' : 'Сохранить',
    saving: busy,
  });

  // Клавиша приходит раньше, чем поле «Сумма» отдало окну набранное (фиксируется на blur), а
  // замыкания биндингов — от прошлой перерисовки: сначала фиксируем активное поле, затем берём
  // обработчик из `latest` (свежий после перерисовки). Каждый обработчик ещё раз проверяет `busy`
  // и полноту. Подробно — в DocumentWindow.
  const latest = useLatest({ handlePost, handleSaveDraft, handleSaveRepost, handleUnpost, handleCancel, handleDelete, handleReturnToDraft, handleRestore });
  const committed = (pick: (h: typeof latest.current) => unknown) => () => {
    if (commitActiveField().invalid) {
      message.warning(INVALID_INPUTS_HINT);
      return;
    }
    void pick(latest.current);
  };
  const notSaved = documentId ? false : 'Документ ещё не сохранён';
  const actions = useHotkeyActions(
    isDeleted
      ? { 'doc.primary': { label: 'Восстановить', hint: false, run: committed((h) => h.handleRestore()) } }
      : status === 'DRAFT'
        ? {
            'doc.primary': { alwaysRun: true, disabled: postBlockReason ?? false, run: committed((h) => h.handlePost(true)) },
            'doc.post': { alwaysRun: true, disabled: postBlockReason ?? false, run: committed((h) => h.handlePost(false)) },
            'doc.save': { label: 'Сохранить черновик', alwaysRun: true, disabled: saveBlockReason ?? false, run: committed((h) => h.handleSaveDraft(false)) },
            'doc.delete': { hint: false, disabled: notSaved, run: committed((h) => h.handleDelete()) },
          }
        : status === 'POSTED'
          ? {
              'doc.primary': { label: 'Сохранить и закрыть', alwaysRun: true, disabled: postBlockReason ?? false, run: committed((h) => h.handleSaveRepost(true)) },
              'doc.save': { label: 'Сохранить', alwaysRun: true, disabled: postBlockReason ?? false, run: committed((h) => h.handleSaveRepost(false)) },
              'doc.unpost': { hint: false, run: committed((h) => h.handleUnpost()) },
              'doc.cancel': { hint: false, run: committed((h) => h.handleCancel()) },
              'doc.delete': { hint: false, run: committed((h) => h.handleDelete()) },
            }
          : { 'doc.primary': { label: 'Вернуть в черновик', hint: false, run: committed((h) => h.handleReturnToDraft()) } },
  );

  return (
    <>
      <PreviewConsequencesDialog diff={pendingDiff} warningTexts={pendingDiff ? warningTexts(pendingDiff.warnings) : []} onResolve={resolvePending} />
      {unsavedDialog}
      <FloatingWindow
        kind={`document:${type}`}
        title={windowTitle(type, doc, isNew)}
        onClose={requestClose}
        footer={renderFooter()}
        hotkeys={actions.bindings}
        initialFocus={isNew ? 'auto' : 'window'}
      >
        <InvalidInputsContext.Provider value={invalidInputs.api}>
          <Flex vertical gap={8}>
            {isNew ? (
              <Segmented
                value={header.cashOperationType}
                onChange={(v) => editHeader({ cashOperationType: v as CashOrderHeader['cashOperationType'] })}
                options={[
                  { value: 'PAYMENT', label: 'Оплата' },
                  { value: 'REFUND', label: 'Возврат денег' },
                ]}
              />
            ) : (
              <Typography.Text style={{ fontSize: 12 }} type="secondary">
                Операция: {header.cashOperationType === 'PAYMENT' ? 'Оплата' : 'Возврат денег'}
              </Typography.Text>
            )}

            <Flex gap={8} wrap>
              <Flex vertical style={{ minWidth: 220, flex: 1 }}>
                <Typography.Text style={{ fontSize: 12 }}>Контрагент</Typography.Text>
                <CounterpartySelect
                  value={header.counterpartyId}
                  current={counterpartyRef}
                  disabled={isReadOnly}
                  onChange={(v) => editHeader({ counterpartyId: v })}
                />
              </Flex>
              <Flex vertical style={{ minWidth: 140 }}>
                <Typography.Text style={{ fontSize: 12 }}>Дата</Typography.Text>
                <DatePicker
                  value={dayjs(header.docDate)}
                  format="DD.MM.YYYY"
                  disabled={isReadOnly}
                  onChange={(d) => editHeader(d ? { docDate: d.format('YYYY-MM-DD') } : {})}
                />
              </Flex>
            </Flex>

            <Flex gap={8} wrap>
              {/* Новый ордер открывается на «Сумма»: контрагент и вид чаще всего уже подставлены основанием. */}
              <Flex vertical style={{ minWidth: 140 }} data-autofocus>
                <Typography.Text style={{ fontSize: 12 }}>Сумма</Typography.Text>
                <MoneyInput value={header.amount} disabled={isReadOnly} onChange={(v) => editHeader({ amount: v })} />
              </Flex>
              <Flex vertical style={{ minWidth: 160 }}>
                <Typography.Text style={{ fontSize: 12 }}>Способ</Typography.Text>
                <Select<PaymentMethod>
                  value={header.method}
                  disabled={isReadOnly}
                  options={METHOD_OPTIONS}
                  style={{ width: '100%' }}
                  onChange={(v) => editHeader({ method: v })}
                />
              </Flex>
              <Flex vertical style={{ minWidth: 200, flex: 1 }}>
                <Typography.Text style={{ fontSize: 12 }}>Комментарий</Typography.Text>
                <Input value={header.comment} disabled={isReadOnly} onChange={(e) => editHeader({ comment: e.target.value })} />
              </Flex>
            </Flex>

            {header.cashOperationType === 'PAYMENT' ? (
              <Checkbox
                checked={noAutoAllocate}
                disabled={isReadOnly}
                onChange={(e) => {
                  setDirty(true);
                  setNoAutoAllocate(e.target.checked);
                }}
              >
                Не разносить (оставить свободным авансом)
              </Checkbox>
            ) : null}

            {header.counterpartyId && !(header.cashOperationType === 'PAYMENT' && noAutoAllocate) ? (
              <AllocationGrid
                title={target.title}
                remainderTitle={target.remainderTitle}
                emptyText={header.cashOperationType === 'REFUND' ? 'Нет незакрытых возвратов' : undefined}
                rows={rows}
                onChange={editRows}
              />
            ) : null}

            {invalidInputs.hasInvalid ? (
              <Typography.Text type="danger" style={{ fontSize: 12 }}>
                {INVALID_INPUTS_HINT}
              </Typography.Text>
            ) : null}

            <AttachmentsBlock documentId={documentId} readOnly={isReadOnly} />
          </Flex>
        </InvalidInputsContext.Provider>
      </FloatingWindow>
    </>
  );

  function renderFooter() {
    if (isFetching && documentId) return <Typography.Text type="secondary">Загрузка…</Typography.Text>;

    const closeButton = (
      <HotkeyTip catalogId="window.close">
        <Button onClick={requestClose}>Закрыть</Button>
      </HotkeyTip>
    );

    if (isDeleted) {
      return (
        <Space wrap>
          <HotkeyButton type="primary" loading={busy} action={actions.get('doc.primary')}>
            Восстановить
          </HotkeyButton>
          {closeButton}
        </Space>
      );
    }
    if (status === 'DRAFT') {
      return (
        <Space wrap>
          <HotkeyButton type="primary" loading={busy} action={actions.get('doc.primary')}>
            Провести и закрыть
          </HotkeyButton>
          <HotkeyButton loading={busy} action={actions.get('doc.post')}>
            Провести
          </HotkeyButton>
          <HotkeyButton loading={busy} action={actions.get('doc.save')}>
            Сохранить черновик
          </HotkeyButton>
          <HotkeyButton danger loading={busy} action={actions.get('doc.delete')}>
            Удалить
          </HotkeyButton>
          {closeButton}
        </Space>
      );
    }
    if (status === 'POSTED') {
      return (
        <Space wrap>
          <HotkeyButton type="primary" loading={busy} action={actions.get('doc.primary')}>
            Сохранить и закрыть
          </HotkeyButton>
          <HotkeyButton loading={busy} action={actions.get('doc.save')}>
            Сохранить
          </HotkeyButton>
          <HotkeyButton loading={busy} action={actions.get('doc.unpost')}>
            Снять проведение
          </HotkeyButton>
          <HotkeyButton loading={busy} action={actions.get('doc.cancel')}>
            Отменить
          </HotkeyButton>
          <HotkeyButton danger loading={busy} action={actions.get('doc.delete')}>
            Удалить
          </HotkeyButton>
          {closeButton}
        </Space>
      );
    }
    // CANCELLED
    return (
      <Space wrap>
        <HotkeyButton loading={busy} action={actions.get('doc.primary')}>
          Вернуть в черновик
        </HotkeyButton>
        {closeButton}
      </Space>
    );
  }
}
